import React, { useEffect, useState, useCallback } from "react";
import useAPI from "../../Hooks/useAPI";
import "./RoleEditor.css";
import { useNavigate } from "react-router-dom";
import { default as Footer } from "../../Components/Footer/FooterSmall";
import '../../Components/Page/Page.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faEdit, faPlus, faUserTag } from '@fortawesome/free-solid-svg-icons';
import { useUser } from "../../Context/UserContext";

type Role = { id: string; name: string };

export const RoleEditor: React.FC = () => {
  const { getRoles, createRole, updateRole, deleteRole } = useAPI();
  const { user, hasRole } = useUser();
  const navigate = useNavigate();

  const [roles, setRoles] = useState<Role[]>([]);
  const [newRoleName, setNewRoleName] = useState("");
  const [editingRoleId, setEditingRoleId] = useState<string | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  const loadRoles = useCallback(async () => {
    const data = await getRoles();
    if (data) setRoles(data);
  }, [getRoles]);

  useEffect(() => {
    // Redirect if not admin
    if (!hasRole('administrator')) {
      navigate('/');
      return;
    }
  }, [user?.roles, navigate]);

  useEffect(() => {
    loadRoles();
  }, [loadRoles]);

  const handleSaveRole = async () => {
    if (editingRoleId) {
      await updateRole(editingRoleId, newRoleName);
    } else {
      await createRole(newRoleName);
    }
    setNewRoleName("");
    setEditingRoleId(null);
    await loadRoles();
  };

  const handleDeleteRole = async () => {
    if (deleteConfirmId) {
      await deleteRole(deleteConfirmId);
      setDeleteConfirmId(null);
      await loadRoles();
    }
  };

  const confirmDelete = (id: string) => setDeleteConfirmId(id);
  const cancelDelete = () => setDeleteConfirmId(null);

  const startEdit = (role: Role) => {
    setEditingRoleId(role.id);
    setNewRoleName(role.name);
  };

  const cancelEdit = () => {
    setNewRoleName("");
    setEditingRoleId(null);
  };

  return (
    <div className='generalPage'>
      <div className='pageContainer'>
      {/*   <Header /> */}
        <div className="button-container">
          <button 
            className="back-button" 
            onClick={() => navigate('/user-admin')}
          >
            <FontAwesomeIcon icon={faArrowLeft} /> Back to Dashboard
          </button>
        </div>
        <div className="role-permission-editor">
          <div className="section-container">
            <h2><FontAwesomeIcon icon={faUserTag} /> Manage Roles</h2>
            <ul className="role-list">
              {roles.map((role) => (
                <li key={role.id}>
                  <span className="role-name">{role.name}</span>
                  <div>
                    <button
                      onClick={() => startEdit(role)}
                      className="edit-button"
                    >
                      <FontAwesomeIcon icon={faEdit} /> Edit
                    </button>
                    <button
                      onClick={() => confirmDelete(role.id)}
                      className="delete-button"
                    >
                      Delete
                    </button>
                  </div>
                </li>
              ))}
            </ul>

            <div className="form-controls">
              <div className="permission-inputs">
                <input
                  type="text"
                  value={newRoleName}
                  onChange={(e) => setNewRoleName(e.target.value)}
                  placeholder="Role name"
                  className="input-field"
                />
              </div>
              <div className="button-group">
                <button
                  onClick={handleSaveRole}
                  className="save-role-button"
                >
                  <FontAwesomeIcon icon={faPlus} /> {editingRoleId ? "Update Role" : "Add Role"}
                </button>
                {editingRoleId && (
                  <button onClick={cancelEdit} className="cancel-button">
                    Cancel
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirmId && (
        <div className="modal-overlay">
          <div className="modal-content">
            <p className="modal-message">Are you sure you want to delete this role?</p>
            <div className="modal-actions">
              <button
                onClick={cancelDelete}
                className="cancel-button"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteRole}
                className="confirm-delete-button"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
